import {stableStringify} from '@sail/utils';
import {buildQueryAndVariablesHash} from 'src/internal/revamp/queryUtils/queryAndVariablesHashing';

import type {
  ErrorExtensionsOfDoc,
  GraphQlDocument,
  VariablesOfDoc,
} from 'src/internal/revamp/graphql/types';
import type {GraphQlOperationErrors} from 'src/internal/revamp/graphql/types/errors';

type Observer = (errors: GraphQlOperationErrors<any> | null) => void;
type ErrorMap = Map<string, GraphQlOperationErrors<any>>;

export default class ErrorCache {
  private errorMap: ErrorMap = new Map();
  private observers: Map<string, Set<Observer>> = new Map();

  setErrors<TDocumentNode extends GraphQlDocument<any, any, any, any>>(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>>,
  ): void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    const currentErrors = this.errorMap.get(key);

    if (stableStringify(currentErrors) !== stableStringify(errors)) {
      this.errorMap.set(key, errors);
      this.notifyObservers(key, errors);
    }
  }

  clearErrors<TDocumentNode extends GraphQlDocument<any, any, any, any>>(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    if (this.errorMap.has(key)) {
      this.errorMap.delete(key);
      this.notifyObservers(key, null);
    }
  }

  getErrors<TDocumentNode extends GraphQlDocument<any, any, any, any>>(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
  ): GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );
    return this.errorMap.get(key) || null;
  }

  observe<TDocumentNode extends GraphQlDocument<any, any, any, any>>(
    query: TDocumentNode,
    variables: VariablesOfDoc<TDocumentNode> | undefined,
    observer: Observer,
  ): () => void {
    const {documentHashWithVariables: key} = buildQueryAndVariablesHash(
      query,
      variables,
    );

    if (!this.observers.has(key)) {
      this.observers.set(key, new Set());
    }
    this.observers.get(key)!.add(observer);

    return () => {
      const observers = this.observers.get(key);

      if (observers) {
        observers.delete(observer);

        if (observers.size === 0) {
          this.observers.delete(key);
        }
      }
    };
  }

  private notifyObservers<
    TDocumentNode extends GraphQlDocument<any, any, any, any>,
  >(
    key: string,
    errors: GraphQlOperationErrors<ErrorExtensionsOfDoc<TDocumentNode>> | null,
  ): void {
    const observers = this.observers.get(key);
    if (observers) {
      observers.forEach((observer) => observer(errors));
    }
  }
}
